import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Eye, Scale } from "lucide-react";
import { colors } from "../lib/theme";

export default function MethodologyPage() {
  return (
    <div className="min-h-screen text-slate-100" style={{ backgroundColor: colors.bgPrimary }}>
      <header className="sticky top-0 z-30 w-full backdrop-blur-sm" style={{
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: `${colors.bgSecondary}80`,
      }}>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{ color: colors.textSecondary }}>
            <ArrowLeft className="h-4 w-4" />
            Back to screener
          </Link>
          <span className="font-semibold tracking-tight" style={{ color: colors.textPrimary }}>Methodology</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <section>
          <h1 className="text-3xl font-bold" style={{ color: colors.textPrimary }}>Screening Methodology</h1>
          <p className="mt-4 leading-relaxed" style={{ color: colors.textSecondary }}>
            EthicCheck screens companies across multiple ethical lenses using publicly available data.
            Each lens has defined rules, thresholds, and data sources. Results are <strong style={{ color: colors.textPrimary }}>PASS</strong>,{" "}
            <strong style={{ color: colors.yellow }}>REVIEW</strong>, or{" "}
            <strong style={{ color: colors.red }}>EXCLUDED</strong>.
          </p>
          <p className="mt-2 leading-relaxed" style={{ color: colors.textSecondary }}>
            This page describes only lenses that are currently implemented and backed by data.
            If a company is not in our database, it receives <strong style={{ color: colors.yellow }}>REVIEW</strong> — never a false PASS.
          </p>
          <p className="mt-2 leading-relaxed" style={{ color: colors.textSecondary }}>
            <strong style={{ color: colors.textPrimary }}>Coverage:</strong> the company master prioritizes
            US-listed large caps (S&amp;P 500 constituents plus a broader US equity ticker set). Unknown or
            non-US tickers may return <strong style={{ color: colors.yellow }}>REVIEW</strong> until coverage expands.
          </p>
        </section>

        <LensSection
          icon={<Shield className="h-5 w-5" />}
          title="BDS-Related Activity"
          color={colors.red}
          id="bds"
          sources={[
            { name: "AFSC Investigate", url: "https://investigate.afsc.org" },
            { name: "Who Profits", url: "https://whoprofits.org" },
            {
              name: "OHCHR A/HRC/60/19 (UN HRC database)",
              url: "https://www.ohchr.org/en/documents/thematic-reports/ahrc6019-database-all-business-enterprises-involved-activities-detailed",
            },
          ]}
        >
          <p>
            Screens for corporate involvement in occupation and settlements using curated list evidence
            (BDS movement sources and the UN OHCHR business database) — not a filter on companies by
            country or region. Data from human rights research organizations and UN mandate reporting.
          </p>
          <p>
            The BDS Movement&apos;s public <em>consumer boycott priority</em> targets are campaign-focused
            and are not ingested as a separate equity list. For investor/divestment screening, the movement
            points to AFSC Investigate, Who Profits, and the UN OHCHR database — sources we already load.
            See backlog 1.5.
          </p>

          <RuleBlock title="Evidence categories">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Economic exploitation</strong> — profiting from occupied territories</li>
              <li><strong>Exploitation of occupied resources</strong> — sourcing from settlements</li>
              <li><strong>Settlement enterprise</strong> — direct settlement involvement</li>
              <li><strong>Israeli construction on occupied land</strong> — construction projects</li>
              <li><strong>Services to settlements</strong> — banking, utilities, transportation</li>
              <li><strong>Other BDS activities</strong> — unclassified involvement</li>
            </ul>
          </RuleBlock>

          <RuleBlock title="Verdict logic">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>EXCLUDED</strong> — HIGH or MEDIUM strength evidence in any category</li>
              <li><strong>REVIEW</strong> — LOW strength evidence only</li>
              <li><strong>PASS</strong> — no BDS evidence found</li>
            </ul>
          </RuleBlock>
        </LensSection>

        <LensSection
          icon={<Eye className="h-5 w-5" />}
          title="Defense Contractors"
          color={colors.yellow}
          id="defense"
          sources={[
            { name: "SIPRI Arms Industry Database", url: "https://www.sipri.org/databases/armsindustry" },
          ]}
        >
          <p>
            Identifies major defense contractors using SIPRI arms industry rankings.
            DoD contract awards from USAspending are applied when loaded for a company; coverage is still expanding.
          </p>

          <RuleBlock title="Thresholds">
            <table className="w-full text-sm mt-2">
              <tbody>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4 font-medium" style={{ color: colors.textPrimary }}>SIPRI Top-100</td>
                  <td className="py-2" style={{ color: colors.textSecondary }}>EXCLUDED — ranked in SIPRI's top 100 arms producers</td>
                </tr>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4 font-medium" style={{ color: colors.textPrimary }}>DoD contracts &ge; $10M</td>
                  <td className="py-2" style={{ color: colors.textSecondary }}>EXCLUDED — when USAspending contract data is loaded for the company</td>
                </tr>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4 font-medium" style={{ color: colors.textPrimary }}>DoD contracts $1M – $10M</td>
                  <td className="py-2" style={{ color: colors.textSecondary }}>REVIEW — when contract data is loaded</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium" style={{ color: colors.textPrimary }}>No SIPRI rank or loaded contracts</td>
                  <td className="py-2" style={{ color: colors.textSecondary }}>PASS (no defense flag from current data)</td>
                </tr>
              </tbody>
            </table>
          </RuleBlock>

          <RuleBlock title="Contract matching">
            <p>When USAspending data is present, contracts are matched by agency name containing "Defense" or "DoD", or by PSC code starting with "10" (weapons/ammunition).</p>
          </RuleBlock>
        </LensSection>

        <LensSection
          icon={<Scale className="h-5 w-5" />}
          title="Shariah Compliance"
          color={colors.green}
          id="shariah"
          sources={[
            {
              name: "S&P Dow Jones Islamic Market methodology",
              url: "https://www.spglobal.com/spdji/en/indices/equity/dow-jones-islamic-market-world-index/",
            },
          ]}
        >
          <p>
            Screens using <strong>DJIM/S&amp;P-style</strong> financial ratios (33% / 33% / 49%) and
            curated haram business activity evidence. This is a <em>screening aid</em>, not a fatwa
            or halal certification. It is <strong>not</strong> AAOIFI Standard No. 21 (30% / 30% / 5%,
            no receivables ratio).
          </p>
          <p>
            We also apply a <strong>5% interest-income / revenue</strong> screen (FMP income statement,
            Yahoo timeseries fallback when FMP is missing or reports 0). We do <strong>not</strong>
            compute haram-activity revenue as a share of total revenue — that is not a statement line
            item. A <strong>PASS</strong> is not parity with Zoya, Musaffa, or AAOIFI.
          </p>

          <RuleBlock title="Business screen">
            <p>If SHARIAH-tagged evidence with a "haram" subtype exists (banks, alcohol, gambling, tobacco, etc.), the company is <strong>EXCLUDED</strong>.</p>
          </RuleBlock>

          <RuleBlock title="Financial ratios (DJIM/S&amp;P-style thresholds)">
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <th className="py-2 pr-4 text-left font-medium" style={{ color: colors.textPrimary }}>Ratio</th>
                  <th className="py-2 text-left font-medium" style={{ color: colors.textPrimary }}>Maximum</th>
                  <th className="py-2 text-left font-medium" style={{ color: colors.textPrimary }}>Result if exceeded</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4" style={{ color: colors.textSecondary }}>Debt / Market Cap</td>
                  <td className="py-2" style={{ color: colors.textPrimary }}>33%</td>
                  <td className="py-2" style={{ color: colors.red }}>EXCLUDED</td>
                </tr>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4" style={{ color: colors.textSecondary }}>(Cash + Interest-bearing securities) / Market Cap</td>
                  <td className="py-2" style={{ color: colors.textPrimary }}>33%</td>
                  <td className="py-2" style={{ color: colors.red }}>EXCLUDED</td>
                </tr>
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  <td className="py-2 pr-4" style={{ color: colors.textSecondary }}>Receivables / Market Cap</td>
                  <td className="py-2" style={{ color: colors.textPrimary }}>49%</td>
                  <td className="py-2" style={{ color: colors.red }}>EXCLUDED</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4" style={{ color: colors.textSecondary }}>Interest income / Revenue</td>
                  <td className="py-2" style={{ color: colors.textPrimary }}>5%</td>
                  <td className="py-2" style={{ color: colors.red }}>EXCLUDED</td>
                </tr>
              </tbody>
            </table>
          </RuleBlock>

          <RuleBlock title="Data source priority">
            <ol className="list-decimal pl-5 space-y-1">
              <li><strong>Database financials (FMP ingest)</strong> — highest confidence; uses stored period data</li>
              <li><strong>Yahoo Finance fallback</strong> — real quoteSummary fields only; flagged &quot;Estimated&quot; with Medium confidence. No fabricated ratios.</li>
              <li><strong>No data available</strong> — REVIEW with &quot;Insufficient financial data&quot; or &quot;Insufficient income data for 5% interest screen&quot; (never a false PASS)</li>
            </ol>
          </RuleBlock>
        </LensSection>

        <LensSection
          icon={<Eye className="h-5 w-5" />}
          title="Surveillance Technology"
          color={colors.blue}
          id="surveillance"
          sources={[
            { name: "EFF Atlas of Surveillance", url: "https://atlasofsurveillance.org" },
          ]}
        >
          <p>
            Planned for a post-launch release. Surveillance screening is not enabled in MVP 1 — the UI toggle
            is hidden until Phase 3 data (EFF Atlas) is loaded. No live EFF Atlas evidence is claimed today.
          </p>

          <RuleBlock title="Planned verdict logic (when enabled)">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>EXCLUDED</strong> — evidence of invasive types: facial recognition, spyware, phone extraction</li>
              <li><strong>REVIEW</strong> — other surveillance technology evidence</li>
              <li><strong>PASS</strong> — no surveillance evidence found</li>
            </ul>
          </RuleBlock>
        </LensSection>

        <section className="rounded-2xl p-6" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>ETF Lookthrough</h2>
          <p className="mt-2" style={{ color: colors.textSecondary }}>
            When an ETF symbol is submitted, EthicCheck looks through to the underlying holdings and screens each one.
            Verdicts are computed <strong style={{ color: colors.textPrimary }}>per lens</strong> (occupation, defense, Shariah, etc.) from the aggregate weight of flagged holdings:
          </p>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-sm" style={{ color: colors.textSecondary }}>
            <li><strong style={{ color: colors.textPrimary }}>&ge;15% excluded weight</strong> — that lens is excluded for the ETF</li>
            <li><strong style={{ color: colors.textPrimary }}>&ge;5% excluded or &ge;10% review weight</strong> — that lens is review</li>
            <li><strong style={{ color: colors.textPrimary }}>Below thresholds</strong> — that lens passes</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: colors.textMuted }}>
            ETF lookthrough requires holdings data loaded in the database. Coverage is expanding.
          </p>
        </section>

        <section className="rounded-2xl p-6" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>Portfolio compliance score</h2>
          <p className="mt-2 text-sm" style={{ color: colors.textSecondary }}>
            When you upload a CSV with <strong style={{ color: colors.textPrimary }}>Market Value</strong> (or Quantity and Price), EthicCheck weights each holding by dollars.
            Otherwise each ticker is weighted equally and the UI notes that the score is estimated.
          </p>
          <p className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
            For each enabled lens, flagged exposure is: 100% of position value for excluded direct holdings, 50% for review, and for ETFs the position value times the lens-specific excluded/review percentage inside the fund.
            Lens score = 100 &times; (1 &minus; (excluded$ + 0.5 &times; review$) / eligible$). Overall score is the average across enabled lenses.
          </p>
        </section>

        <section className="rounded-2xl p-6" style={{ backgroundColor: `${colors.yellow}10`, border: `1px solid ${colors.yellow}30` }}>
          <h2 className="text-lg font-semibold" style={{ color: colors.yellow }}>Disclaimer</h2>
          <p className="mt-2 text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
            EthicCheck is a <strong style={{ color: colors.textPrimary }}>screening tool</strong>, not financial advice, legal counsel, or religious certification.
            Results are based on best-effort research from publicly available sources and may contain gaps or errors.
            Always verify results independently before making investment decisions.
            Shariah screening is a computational aid based on DJIM/S&amp;P-style ratios — it is not a fatwa or halal certification.
            See <Link to="/security" className="underline hover:opacity-80" style={{ color: colors.textPrimary }}>Security &amp; privacy</Link> for data handling.
          </p>
        </section>
      </main>
    </div>
  );
}

function LensSection({
  icon,
  title,
  color,
  id,
  sources,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  color: string;
  id: string;
  sources: { name: string; url: string }[];
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="rounded-2xl p-6 space-y-4" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 grid place-items-center rounded-lg" style={{ backgroundColor: `${color}20`, color }}>
          {icon}
        </div>
        <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{title}</h2>
      </div>

      <div className="space-y-4 text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
        {children}
      </div>

      <div className="pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
        <div className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>Sources</div>
        <div className="mt-1 flex flex-wrap gap-2">
          {sources.map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md px-2.5 py-1 text-xs hover:opacity-80 transition-opacity"
              style={{ border: `1px solid ${colors.border}`, color: colors.blue, backgroundColor: colors.bgTertiary }}
            >
              {s.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function RuleBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg p-4" style={{ backgroundColor: colors.bgTertiary }}>
      <div className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: colors.textMuted }}>{title}</div>
      <div style={{ color: colors.textSecondary }}>{children}</div>
    </div>
  );
}
